import { useState } from "react";
import { LogOut, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "./Brand";
import { NavLinks } from "./NavLinks";
import { LanguageToggle } from "./LanguageToggle";
import { useLanguage } from "@/i18n/LanguageProvider";
import { L } from "@/i18n";

export const Topbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { canSwitch } = useLanguage();
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.username;

  const handleLogout = () => {
    logout();
    toast.success(L("Signed out.", "Sessão terminada."));
    navigate("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 h-16 flex items-center gap-3 px-4 sm:px-8 bg-[#0a1425]/90 backdrop-blur-md border-b border-slate-800/80">
      <button
        type="button"
        className="md:hidden p-2 -ml-2 text-slate-300 hover:text-amber-400"
        onClick={() => setOpen(true)}
        data-testid="mobile-menu-button"
        aria-label={L("Open menu", "Abrir menu")}
      >
        <Menu className="h-5 w-5" />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 bg-[#091120] border-slate-800 p-0" data-testid="mobile-sidebar" aria-describedby={undefined}>
          <SheetTitle className="sr-only">{L("Navigation", "Navegação")}</SheetTitle>
          <div className="h-16 flex items-center px-5 border-b border-slate-800/80">
            <Brand />
          </div>
          <div className="p-3">
            <NavLinks prefix="mobile" onNavigate={() => setOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
      <div className="md:hidden"><Brand compact /></div>
      <div className="ml-auto flex items-center gap-3">
        {canSwitch && <LanguageToggle />}
        <div className="hidden sm:block text-right leading-tight">
          <p className="text-sm font-medium text-slate-200" data-testid="topbar-user-name">{name}</p>
          <p className="text-[10px] font-mono uppercase tracking-widest text-amber-500/80">{user?.role}</p>
        </div>
        <div className="h-9 w-9 rounded-full bg-slate-800 border border-slate-700 grid place-items-center text-sm font-semibold text-amber-400">
          {name?.[0]?.toUpperCase()}
        </div>
        <button
          type="button"
          onClick={handleLogout}
          data-testid="logout-button"
          className="p-2 rounded-md text-slate-400 hover:text-rose-400 hover:bg-slate-800/60 transition-colors"
          aria-label={L("Sign out", "Terminar sessão")}
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
};
